import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Car,
  MapPin,
  Bell,
  AlertTriangle,
  Clock,
  Share2,
  TrendingUp,
  ShieldCheck,
  Key,
  Lock,
  Database,
  CloudLightning,
  ChevronDown,
  ArrowRight,
  Menu,
  X,
  Check,
  DownloadCloud,
  Gauge,
  Siren,
  Route,
  Award,
  Languages,
  Play
} from "lucide-react";
import DashboardMockup from "../components/marketing/DashboardMockup";
import AdBanner from "../components/AdBanner";
import siteContent from "../seo/content.json";
import useSeo from "../seo/useSeo";
import infographic from "../assets/infographic.png";
import "../styles/marketing.css";

// ScrollReveal Component using Intersection Observer
function ScrollReveal({ children, className = "", delayClass = "" }) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.05, rootMargin: "0px 0px -50px 0px" }
    );
    if (currentRef) {
      observer.observe(currentRef);
    }
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <div
      ref={ref}
      className={`reveal ${isVisible ? "reveal-visible" : ""} ${delayClass} ${className}`}
    >
      {children}
    </div>
  );
}

// Helper Counter Component for statistics using Intersection Observer
function CountingStat({ value, suffix = "", duration = 1.5 }) {
  const [count, setCount] = useState(0);
  const elementRef = useRef(null);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setHasStarted(true);
        }
      },
      { threshold: 0.1 }
    );
    if (elementRef.current) observer.observe(elementRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!hasStarted) return;
    
    const num = parseFloat(value.replace(/[^0-9.]/g, ""));
    const isFloat = value.includes(".");
    
    let startTimestamp = null;
    let rAFId = null;

    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / (duration * 1000), 1);
      
      const currentVal = progress * num;
      setCount(isFloat ? currentVal.toFixed(1) : Math.floor(currentVal));

      if (progress < 1) {
        rAFId = window.requestAnimationFrame(step);
      }
    };
    
    rAFId = window.requestAnimationFrame(step);
    return () => {
      if (rAFId) {
        window.cancelAnimationFrame(rAFId);
      }
    };
  }, [hasStarted, value, duration]);

  return (
    <span ref={elementRef} className="font-extrabold tabular-nums">
      {count}{suffix}
    </span>
  );
}

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);

  useSeo("/");

  // Shared with /faq and scripts/prerender.js so the questions that get
  // indexed are byte-identical to the ones users see.
  const faqs = siteContent.faqs;

  const testimonials = [
    {
      quote: "Converting our delivery cars' standard Android screens into GPS trackers saved us thousands of rupees in proprietary hardware costs. The real-time updates and geofence alerts are incredibly accurate!",
      name: "Rajesh Kumar",
      role: "Fleet Manager",
      avatarBg: "bg-indigo-100 text-indigo-700",
      avatarInitials: "RK"
    },
    {
      quote: "I set up the Telegram Alert bot so I get notified the absolute second the valet starts my engine or leaves the parking geofence. The privacy-first, zero-subscription policy is exactly what I wanted.",
      name: "Siddharth Mehta",
      role: "Vehicle Owner",
      avatarBg: "bg-purple-100 text-purple-700",
      avatarInitials: "SM"
    },
    {
      quote: "Installed the APK via USB on my dashboard in 5 minutes. The continuous background location transmission is extremely robust, even when using other navigation apps on the head unit.",
      name: "Amit Sharma",
      role: "Taxi Driver",
      avatarBg: "bg-cyan-100 text-cyan-700",
      avatarInitials: "AS"
    }
  ];

  const features = [
    {
      icon: MapPin,
      title: "Live GPS Tracking",
      desc: "Track coordinates continuously using Leaflet maps with sub-10 second updates. Store complete travel routes in your private database."
    },
    {
      icon: Car,
      title: "Geofence Monitoring",
      desc: "Set boundaries around important parking zones. Receive instant notification alerts whenever a vehicle crosses the virtual boundary limits."
    },
    {
      icon: AlertTriangle,
      title: "SOS Emergency Alerts",
      desc: "A single tap on your vehicle screen fires instant distress warnings. Automatically notifies all emergency contacts via chat links."
    },
    {
      icon: Clock,
      title: "Trip History",
      desc: "Review complete details of all past driving logs. Monitor exact ignition triggers, idle zones, speeds, and total distance driven."
    },
    {
      icon: Share2,
      title: "Live Location Sharing",
      desc: "Generate temporary, secure tracking links (15, 30, or 60 minutes) to share your vehicle's live coordinates with friends, family, or customers."
    },
    {
      icon: TrendingUp,
      title: "Vehicle Analytics",
      desc: "Access deep analytics on overall vehicle usage, overspeed warnings, and driver behavior scores to optimize operations."
    },
  ];

  return (
    <div className="relative min-h-screen bg-surface-soft selection:bg-brand-600/20 selection:text-brand-600 font-body">
      
      {/* ==================== NAVBAR ==================== */}
      <header className="sticky top-0 z-50 border-b border-white/5 bg-slate-950/80 backdrop-blur-xl text-white">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-brand-500 to-accent-cyan flex items-center justify-center shadow-brand transition-transform group-hover:scale-105 duration-300">
              <img src="/logo192.png" alt="" className="w-5 h-5 object-contain" />
            </div>
            <span className="text-[15px] font-heading font-extrabold text-white tracking-tight group-hover:text-brand-400 transition-colors">CarTrack</span>
          </Link>

          <nav className="hidden md:flex items-center gap-7 text-[13px] font-medium text-slate-300">
            <a href="#features-detail" className="hover:text-white transition-colors duration-150">Features</a>
            <Link to="/faq" className="hover:text-white transition-colors duration-150">FAQ</Link>
            <Link to="/user-manual" className="hover:text-white transition-colors duration-150">Docs</Link>
            <Link to="/about-us" className="hover:text-white transition-colors duration-150">About</Link>
            <Link to="/privacy-policy" className="hover:text-white transition-colors duration-150">Privacy</Link>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Link to="/login" className="text-[13px] font-semibold text-slate-300 hover:text-white transition-colors px-3 py-1.5">
              Sign In
            </Link>
            <Link to="/signup" className="text-[13px] font-semibold px-4 py-2 rounded-xl bg-gradient-to-r from-brand-600 to-accent-cyan text-white hover:opacity-95 transition-opacity shadow-brand cursor-pointer shimmer-btn">
              Get Started
            </Link>
          </div>

          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
            className="md:hidden p-2 rounded-lg hover:bg-white/5 transition-colors text-slate-300 cursor-pointer" 
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, y: -8 }} 
              animate={{ opacity: 1, y: 0 }} 
              exit={{ opacity: 0, y: -8 }} 
              transition={{ duration: 0.18 }}
              className="md:hidden bg-slate-950 border-t border-white/5 px-6 py-5 space-y-1"
            >
              {["FAQ:/faq", "Docs:/user-manual", "About:/about-us", "Privacy:/privacy-policy"].map(item => {
                const [label, path] = item.split(":");
                return (
                  <Link key={path} to={path} onClick={() => setMobileMenuOpen(false)}
                    className="block py-2.5 text-[14px] font-medium text-slate-300 hover:text-white transition-colors">
                    {label}
                  </Link>
                );
              })}
              <div className="pt-4 space-y-2 border-t border-white/5 mt-3">
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} 
                  className="block w-full py-2.5 text-center text-[13px] font-semibold text-white border border-white/10 rounded-xl hover:bg-white/5 transition-colors">
                  Sign In
                </Link>
                <Link to="/signup" onClick={() => setMobileMenuOpen(false)} 
                  className="block w-full py-2.5 text-center text-[13px] font-semibold text-white bg-gradient-to-r from-brand-600 to-accent-cyan rounded-xl hover:opacity-95 transition-opacity shadow-brand">
                  Get Started Free
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* ==================== SECTION 1: HERO ==================== */}
      <section className="relative overflow-hidden pt-24 pb-28 md:pt-32 md:pb-36 bg-mesh-gradient text-white">
        {/* Floating gradient lights */}
        <div className="particle-orb top-12 left-1/4 w-80 h-80 bg-brand-500/20" />
        <div className="particle-orb bottom-12 right-1/4 w-96 h-96 bg-accent-cyan/20" style={{ animationDelay: "2s" }} />

        <div className="relative max-w-7xl mx-auto px-6 flex flex-col items-center text-center">
          
          <ScrollReveal>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 mb-7 backdrop-blur-md">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-emerald pulse-green" />
              <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-300 font-body">Live GPS Tracking Platform</span>
            </div>
          </ScrollReveal>

          <ScrollReveal delayClass="reveal-delay-100">
            {/* Keyword-bearing H1 — kept identical to the prerendered H1 in
                src/seo/content.json so crawlers and users see the same thing. */}
            <h1 className="font-heading text-[38px] sm:text-[52px] md:text-[64px] font-black tracking-[-0.03em] text-white leading-[1.06] max-w-4xl">
              Free Car GPS Tracker App
              <br />
              <span className="gradient-text font-black">for Android Head Units</span>
            </h1>
          </ScrollReveal>

          <ScrollReveal delayClass="reveal-delay-200">
            <p className="mt-6 text-slate-300 text-[16px] md:text-[19px] max-w-2xl leading-[1.65] font-body">
              Track your car in real time using the Android screen already in it. Live GPS
              location, trip history, geofence and overspeed alerts, SOS and Telegram
              notifications — no tracking device to buy, no monthly subscription.
            </p>
          </ScrollReveal>

          <ScrollReveal delayClass="reveal-delay-300">
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/signup"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-xl bg-gradient-to-r from-brand-600 to-accent-cyan text-white text-[14px] font-bold hover:opacity-95 transition-opacity shadow-brand group cursor-pointer shimmer-btn">
                Start Tracking Free
                <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <a href="https://play.google.com/store/apps/details?id=com.cartrack.tracker" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-xl border border-white/10 bg-white/5 text-white text-[14px] font-semibold hover:bg-white/10 transition-colors shadow-lg cursor-pointer backdrop-blur-md">
                <Play size={14} className="fill-current" />
                Google Play
              </a>
              <a href="/app-release.apk" download
                className="inline-flex items-center gap-2 px-7 py-4 rounded-xl border border-white/10 bg-white/5 text-white text-[14px] font-semibold hover:bg-white/10 transition-colors shadow-lg cursor-pointer backdrop-blur-md">
                <DownloadCloud size={14} />
                Download APK
              </a>
            </div>
          </ScrollReveal>

          <ScrollReveal delayClass="reveal-delay-400" className="w-full max-w-4xl mx-auto">
            <div className="mt-16 relative">
              {/* Outer soft glow behind the mockup */}
              <div className="absolute inset-0 bg-gradient-to-tr from-brand-500/10 to-accent-cyan/10 rounded-2xl filter blur-2xl -z-10" />
              <div className="rounded-2xl border border-white/5 shadow-glow-md overflow-hidden bg-dark-card/45 backdrop-blur-md p-2">
                <div className="rounded-xl overflow-hidden border border-white/5">
                  <DashboardMockup />
                </div>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delayClass="reveal-delay-500" className="max-w-2xl w-full mx-auto">
            <div className="mt-12 rounded-2xl border border-brand-500/20 bg-brand-950/20 backdrop-blur-md p-6 text-left flex gap-4">
              <div className="w-8 h-8 rounded-lg bg-brand-500/10 text-brand-400 flex items-center justify-center shrink-0 mt-0.5 border border-brand-500/20">
                <AlertTriangle size={15} />
              </div>
              <div>
                <p className="text-[13px] font-bold text-white mb-2">Hardware & Network Requirements</p>
                <ul className="space-y-1.5 text-[12px] text-slate-400 leading-relaxed">
                  <li><span className="font-medium text-slate-200">Android Head Units only</span> — not for standard mobile phones.</li>
                  <li><span className="font-medium text-slate-200">Active internet required</span> — 4G SIM, 5G, or shared Wi-Fi hotspot.</li>
                  <li>Search <span className="font-medium text-accent-cyan">"CarTrack - Live Car GPS Tracker"</span> on Google Play if available.</li>
                </ul>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ==================== SECTION 2: TRUST BAR ==================== */}
      <section className="py-12 border-y border-surface-border bg-surface-soft overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <ScrollReveal>
            <p className="text-center text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-muted mb-8 font-body">
              Powered by proven open-source infrastructure
            </p>
            
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
              {[
                { name: "React", icon: (
                  <svg className="w-6 h-6 animate-[spin_20s_linear_infinite]" viewBox="-11.5 -10.23174 23 20.46348">
                    <circle cx="0" cy="0" r="2.05" fill="#4f46e5"/>
                    <g stroke="#4f46e5" strokeWidth="1" fill="none">
                      <ellipse rx="11" ry="4.2"/>
                      <ellipse rx="11" ry="4.2" transform="rotate(60)"/>
                      <ellipse rx="11" ry="4.2" transform="rotate(120)"/>
                    </g>
                  </svg>
                )},
                { name: "FastAPI", icon: (
                  <svg className="w-5 h-5 text-emerald-600 fill-current" viewBox="0 0 24 24">
                    <path d="M12 2L2 22h20L12 2zm1 16h-2v-2h-1v-2h3v4zm0-6h-2V7h2v5z"/>
                  </svg>
                )},
                { name: "Telegram", icon: (
                  <svg className="w-5 h-5 fill-current text-sky-500" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.11.02-1.93 1.22-5.46 3.6-.52.36-.96.53-1.34.52-.41-.01-1.21-.23-1.8-.42-.72-.24-1.3-.37-1.25-.79.03-.22.33-.44.9-.68 3.52-1.53 5.87-2.54 7.05-3.03 3.35-1.39 4.05-1.63 4.5-.14.04.14.07.3.08.47z"/>
                  </svg>
                )},
                { name: "Google Maps", icon: (
                  <svg className="w-5 h-5 fill-current text-red-500" viewBox="0 0 24 24">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                )},
                { name: "Android", icon: (
                  <svg className="w-5 h-5 fill-current text-green-500" viewBox="0 0 24 24">
                    <path d="M16 1.62c0-.43-.35-.78-.78-.78-.43 0-.78.35-.78.78V4h-4.88V1.62c0-.43-.35-.78-.78-.78s-.78.35-.78.78V4H6c-2.48 0-4.5 2.02-4.5 4.5v7c0 2.48 2.02 4.5 4.5 4.5h12c2.48 0 4.5-2.02 4.5-4.5v-7C22.5 6.02 20.48 4 18 4h-2V1.62zM7.5 11c.83 0 1.5.67 1.5 1.5S8.33 14 7.5 14 6 13.33 6 12.5 6.67 11 7.5 11zm9 0c.83 0 1.5.67 1.5 1.5S17.33 14 16.5 14s-1.5-.67-1.5-1.5.67-1.5 1.5-1.5z"/>
                  </svg>
                )},
                { name: "MongoDB", icon: (
                  <svg className="w-5 h-5 fill-current text-emerald-500" viewBox="0 0 24 24">
                    <path d="M17.15 11.23c-.72-2.82-2.85-6.89-4.55-9.15-.3-.4-.9-.4-1.2 0-1.7 2.26-3.83 6.33-4.55 9.15-.96 3.75-.41 6.84.45 8.3.15.26.43.34.69.21.36-.18.73-.39 1.1-.64.12-.08.27-.08.38 0 1.34.92 2.65 1.48 3.03 1.63.15.06.31.06.46 0 .38-.15 1.69-.71 3.03-1.63.11-.08.26-.08.38 0 .37.25.74.46 1.1.64.26.13.54.05.69-.21.86-1.46 1.41-4.55.45-8.3zm-5.15 6.27V4.54c1.19 1.83 2.76 5.16 3.1 7.37.56 3.65-.6 6.01-3.1 5.59z"/>
                  </svg>
                )}
              ].map((brand, i) => (
                <div key={i} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-surface-border shadow-card-sm hover:border-brand-200 hover:shadow-brand/5 transition-all duration-300 group cursor-default">
                  <div className="transition-transform group-hover:scale-110 duration-300">
                    {brand.icon}
                  </div>
                  <span className="text-[13px] font-semibold text-ink-soft group-hover:text-ink transition-colors">{brand.name}</span>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Ad Banner - Top Placement */}
      <section className="max-w-4xl mx-auto px-6 my-8">
        <AdBanner testid="splash-ad-top" />
      </section>

      {/* ==================== SECTION 3: FEATURE GRID ==================== */}
      <section className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-6">
          <ScrollReveal>
            <div className="text-center max-w-xl mx-auto mb-16">
              <span className="inline-block text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-600 mb-3 px-3 py-1 rounded-full bg-brand-50 border border-brand-100">Features</span>
              <h2 className="font-heading text-[36px] md:text-[44px] font-extrabold text-ink tracking-[-0.02em] leading-[1.15]">
                Full-Featured Fleet Security
              </h2>
              <p className="mt-4 text-[15px] text-ink-soft leading-[1.65] font-body">
                Everything you need to monitor, protect, and manage your vehicles live.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((f, idx) => {
              const Icon = f.icon;
              return (
                <ScrollReveal key={idx} delayClass={`reveal-delay-${(idx % 3) * 100}`}>
                  <div className="group h-full bg-white/70 backdrop-blur-md border border-surface-border rounded-2xl p-8 card-hover-lift hover:border-brand-200/60 cursor-default">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-brand-500/10 to-accent-cyan/10 text-brand-600 flex items-center justify-center mb-6 border border-brand-500/10 transition-all duration-300 group-hover:scale-110 group-hover:from-brand-500 group-hover:to-accent-cyan group-hover:text-white group-hover:shadow-brand/35">
                      <Icon size={20} strokeWidth={2} />
                    </div>
                    <h3 className="text-[16px] font-heading font-bold text-ink mb-3 group-hover:text-brand-600 transition-colors">{f.title}</h3>
                    <p className="text-[13px] text-ink-soft leading-[1.7] font-body">{f.desc}</p>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==================== SECTION 3B: FEATURES IN DEPTH ==================== */}
      <section id="features-detail" className="py-28 bg-surface-soft border-t border-surface-border relative overflow-hidden">
        <div className="absolute top-0 left-1/3 w-[500px] h-[300px] bg-brand-500/[0.04] rounded-full blur-[100px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">

          <ScrollReveal>
            <div className="text-center max-w-xl mx-auto mb-20">
              <span className="inline-block text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-600 mb-3 px-3 py-1 rounded-full bg-brand-50 border border-brand-100">Deep Dive</span>
              <h2 className="font-heading text-[36px] md:text-[44px] font-extrabold text-ink tracking-[-0.02em] leading-[1.15]">
                Every Feature, Explained
              </h2>
              <p className="mt-4 text-[15px] text-ink-soft leading-[1.65] font-body">
                Exactly how CarTrack keeps your vehicle visible, protected, and accountable — down to the second.
              </p>
            </div>
          </ScrollReveal>

          <div className="space-y-24">

            {/* ── Deep dive 1: Live GPS Tracking ── */}
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <ScrollReveal>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-50 border border-brand-100 text-brand-600 text-[11px] font-semibold uppercase tracking-[0.12em] mb-4">
                  <MapPin size={12} /> Live GPS Tracking
                </div>
                <h3 className="font-heading text-[26px] md:text-[32px] font-extrabold text-ink tracking-[-0.02em] leading-[1.2]">
                  Second-by-second location, even offline
                </h3>
                <p className="mt-4 text-[14px] text-ink-soft leading-[1.75] font-body">
                  The head-unit tracker streams GPS coordinates over encrypted HTTPS on a configurable
                  15 / 30 / 60-second interval — auto-tuned per head-unit brand for the best
                  battery-vs-accuracy balance. It works with <em>or without</em> Google Play Services,
                  falling back to the native Android LocationManager on Chinese head units.
                </p>
                <ul className="mt-6 space-y-3">
                  {[
                    "Offline queue stores up to 100 GPS points and auto-syncs when the network returns",
                    "Live dashboard shows speed, GPS signal strength, online status & today's distance",
                    "Foreground service survives reboots, app updates, and “clear recents” swipes",
                    "All timestamps rendered in IST, distances in kilometres",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-5 h-5 rounded-full bg-gradient-to-tr from-brand-500 to-accent-cyan text-white flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                        <Check size={11} strokeWidth={3} />
                      </span>
                      <span className="text-[13px] text-ink-mid font-body leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </ScrollReveal>

              <ScrollReveal delayClass="reveal-delay-200">
                <div className="relative rounded-2xl bg-slate-950 border border-white/5 p-6 shadow-glow-md overflow-hidden animate-float">
                  <div className="absolute top-0 right-0 w-48 h-48 bg-accent-cyan/10 rounded-full blur-[60px]" />
                  {/* mock map grid */}
                  <div className="relative rounded-xl bg-[linear-gradient(rgba(99,102,241,0.07)_1px,transparent_1px),linear-gradient(90deg,rgba(99,102,241,0.07)_1px,transparent_1px)] bg-[size:28px_28px] h-56 border border-white/5 overflow-hidden">
                    {/* animated route line */}
                    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 220" fill="none">
                      <path d="M20 190 C 90 160, 130 120, 200 110 S 330 70, 380 40" stroke="#6366f1" strokeWidth="3" strokeLinecap="round" strokeDasharray="6 8" className="animate-dash-flow" />
                    </svg>
                    {/* pulsing live dot */}
                    <div className="absolute" style={{ top: "16%", right: "6%" }}>
                      <span className="block w-4 h-4 rounded-full bg-accent-cyan pulse-blue border-2 border-white shadow-lg" />
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Current speed</div>
                      <div className="font-mono text-[32px] font-black text-white leading-none mt-1">
                        <CountingStat value="64" duration={2} /><span className="text-[13px] text-slate-400 font-bold ml-1">km/h</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Coordinates</div>
                      <div className="font-mono text-[12px] text-accent-cyan mt-1.5">28.6129, 77.2295</div>
                      <div className="inline-flex items-center gap-1.5 mt-1.5 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent-emerald pulse-green" />
                        <span className="text-[10px] font-bold text-emerald-400">LIVE · updated 4s ago</span>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>

            {/* ── Deep dive 2: Intelligent Alerts ── */}
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <ScrollReveal delayClass="reveal-delay-100" className="order-2 lg:order-1">
                <div className="space-y-3">
                  {[
                    { icon: Car, color: "text-brand-600 bg-brand-50 border-brand-100", bar: "bg-brand-500", title: "Engine ON — Ignition detected", sub: "Swift Dzire started · 60-min live link attached", time: "Just now", pulse: true },
                    { icon: Gauge, color: "text-amber-600 bg-amber-50 border-amber-100", bar: "bg-amber-400", title: "Overspeed: 96 km/h", sub: "Limit 80 km/h · NH-48 near Gurugram", time: "2m ago" },
                    { icon: ShieldCheck, color: "text-red-500 bg-red-50 border-red-100", bar: "bg-red-500", title: "Left safe zone “Home”", sub: "Geofence exit · live tracking link sent", time: "18m ago" },
                    { icon: ShieldCheck, color: "text-emerald-600 bg-emerald-50 border-emerald-100", bar: "bg-emerald-500", title: "Entered safe zone “Office”", sub: "Geofence enter · logged to history", time: "1h ago" },
                  ].map((a, i) => {
                    const Icon = a.icon;
                    return (
                      <div key={i} className="relative bg-white border border-surface-border rounded-2xl overflow-hidden shadow-card-sm hover:shadow-card-md hover:border-brand-200/60 transition-all duration-300" style={{ animationDelay: `${i * 120}ms` }}>
                        <div className={`absolute left-0 top-0 bottom-0 w-1 ${a.bar}`} />
                        <div className="pl-4 pr-4 py-3.5 flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${a.color}`}>
                            <Icon size={17} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-[13px] font-extrabold text-ink truncate">{a.title}</span>
                              {a.pulse && <span className="w-2 h-2 rounded-full bg-brand-600 animate-pulse shrink-0" />}
                            </div>
                            <div className="text-[11px] text-ink-muted mt-0.5 truncate">{a.sub}</div>
                          </div>
                          <span className="text-[10px] font-bold text-ink-subtle shrink-0">{a.time}</span>
                        </div>
                      </div>
                    );
                  })}
                  <div className="flex items-center justify-center gap-2 pt-2">
                    <svg className="w-4 h-4 fill-current text-sky-500" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.11.02-1.93 1.22-5.46 3.6-.52.36-.96.53-1.34.52-.41-.01-1.21-.23-1.8-.42-.72-.24-1.3-.37-1.25-.79.03-.22.33-.44.9-.68 3.52-1.53 5.87-2.54 7.05-3.03 3.35-1.39 4.05-1.63 4.5-.14.04.14.07.3.08.47z"/></svg>
                    <span className="text-[11px] font-semibold text-ink-muted">Delivered instantly on Telegram — in English or हिन्दी</span>
                  </div>
                </div>
              </ScrollReveal>

              <ScrollReveal className="order-1 lg:order-2">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-100 text-amber-600 text-[11px] font-semibold uppercase tracking-[0.12em] mb-4">
                  <Bell size={12} /> Intelligent Alerts
                </div>
                <h3 className="font-heading text-[26px] md:text-[32px] font-extrabold text-ink tracking-[-0.02em] leading-[1.2]">
                  Know the moment anything happens
                </h3>
                <p className="mt-4 text-[14px] text-ink-soft leading-[1.75] font-body">
                  CarTrack watches your vehicle continuously and pushes rich, actionable Telegram
                  notifications the instant something changes — each one with location, IST timestamp,
                  and a tappable live-tracking map link.
                </p>
                <ul className="mt-6 space-y-3">
                  {[
                    "Ignition-ON detection — fires when the engine starts after 5+ minutes of idle, with a 5-minute anti-spam cooldown",
                    "Overspeed alerts with a limit you control (40–140 km/h), debounced to one alert per minute",
                    "Geofence enter & exit alerts — exits include a 60-minute live tracking link",
                    "Bilingual message templates: English and Hindi (हिन्दी)",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-5 h-5 rounded-full bg-gradient-to-tr from-amber-500 to-amber-400 text-white flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                        <Check size={11} strokeWidth={3} />
                      </span>
                      <span className="text-[13px] text-ink-mid font-body leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </ScrollReveal>
            </div>

            {/* ── Deep dive 3: SOS Emergency ── */}
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <ScrollReveal>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-50 border border-red-100 text-red-600 text-[11px] font-semibold uppercase tracking-[0.12em] mb-4">
                  <Siren size={12} /> SOS Emergency Dispatch
                </div>
                <h3 className="font-heading text-[26px] md:text-[32px] font-extrabold text-ink tracking-[-0.02em] leading-[1.2]">
                  One button. Everyone who matters, notified.
                </h3>
                <p className="mt-4 text-[14px] text-ink-soft leading-[1.75] font-body">
                  A prominent SOS button lives on the car's dashboard screen. Tapping it starts a
                  deliberate confirm-and-countdown sequence — so accidental presses never fire —
                  then instantly broadcasts an emergency alert with a live tracking link.
                </p>
                <ul className="mt-6 space-y-3">
                  {[
                    "Double-tap confirmation followed by a cancellable 3-2-1 countdown",
                    "Notifies you plus up to 3 saved emergency contacts on Telegram simultaneously",
                    "Every alert carries a 60-minute live tracking link — no login needed to view",
                    "Complete SOS history log with location, speed & contacts notified",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-5 h-5 rounded-full bg-gradient-to-tr from-red-500 to-red-400 text-white flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                        <Check size={11} strokeWidth={3} />
                      </span>
                      <span className="text-[13px] text-ink-mid font-body leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </ScrollReveal>

              <ScrollReveal delayClass="reveal-delay-200">
                <div className="relative rounded-2xl bg-slate-950 border border-white/5 p-8 shadow-glow-md overflow-hidden flex flex-col items-center">
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(239,68,68,0.12),transparent_65%)]" />
                  <div className="relative flex items-center justify-center my-6">
                    <span className="absolute w-40 h-40 rounded-full border-2 border-red-500/20 animate-ping" style={{ animationDuration: "2.5s" }} />
                    <span className="absolute w-32 h-32 rounded-full border border-red-500/30" />
                    <div className="relative w-24 h-24 rounded-full bg-gradient-to-b from-red-500 to-red-700 flex items-center justify-center shadow-[0_0_50px_rgba(239,68,68,0.45)] cursor-default">
                      <span className="font-heading font-black text-white text-[20px] tracking-widest">SOS</span>
                    </div>
                  </div>
                  <div className="relative flex items-center gap-3 mt-2">
                    {["3", "2", "1"].map((n, i) => (
                      <span key={n} className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center font-mono font-black text-white text-[15px] animate-pulse" style={{ animationDelay: `${i * 400}ms` }}>{n}</span>
                    ))}
                    <ArrowRight size={14} className="text-slate-500" />
                    <span className="px-3 py-2 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-[11px] font-black uppercase tracking-wider">Dispatched</span>
                  </div>
                  <div className="relative mt-6 w-full space-y-2">
                    {["Priya (Wife)", "Arjun (Brother)", "Rohan (Friend)"].map((c, i) => (
                      <div key={c} className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-white/5 border border-white/10">
                        <span className="text-[12px] font-semibold text-slate-300">{c}</span>
                        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-emerald-400">
                          <Check size={11} strokeWidth={3} /> Notified
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </ScrollReveal>
            </div>

            {/* ── Deep dive 4: Trips & Weekly Reports ── */}
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <ScrollReveal delayClass="reveal-delay-100" className="order-2 lg:order-1">
                <div className="rounded-2xl bg-white border border-surface-border p-8 shadow-card-lg relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-56 h-56 bg-brand-500/[0.05] rounded-full blur-[70px]" />
                  <div className="relative flex items-center gap-8">
                    {/* Safety score ring */}
                    <svg viewBox="0 0 100 100" className="w-32 h-32 shrink-0">
                      <circle cx="50" cy="50" r="40" stroke="#e2e8f0" strokeWidth="9" fill="none" />
                      <circle cx="50" cy="50" r="40" stroke="url(#scoreGrad)" strokeWidth="9" fill="none"
                        strokeLinecap="round" strokeDasharray="251.3" strokeDashoffset="30"
                        transform="rotate(-90 50 50)" />
                      <defs>
                        <linearGradient id="scoreGrad" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor="#4f46e5" />
                          <stop offset="100%" stopColor="#06b6d4" />
                        </linearGradient>
                      </defs>
                      <text x="50" y="48" textAnchor="middle" dominantBaseline="middle" className="fill-ink font-black" style={{ fontSize: 24, fontFamily: "Sora" }}>88</text>
                      <text x="50" y="66" textAnchor="middle" className="fill-slate-400" style={{ fontSize: 9, fontWeight: 700 }}>SAFETY SCORE</text>
                    </svg>
                    <div className="flex-1 grid grid-cols-2 gap-3">
                      {[
                        { label: "Distance", value: "212.7", suffix: " km" },
                        { label: "Trips", value: "28", suffix: "" },
                        { label: "Top speed", value: "96", suffix: " km/h" },
                        { label: "Overspeeds", value: "2", suffix: "" },
                      ].map(s => (
                        <div key={s.label} className="bg-surface-soft rounded-xl px-3.5 py-3 border border-surface-border">
                          <div className="font-mono text-[18px] font-black text-brand-600 leading-none">
                            <CountingStat value={s.value} suffix={s.suffix} duration={1.8} />
                          </div>
                          <div className="text-[9px] font-bold uppercase tracking-wider text-ink-muted mt-1.5">{s.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="relative mt-6 flex items-center gap-2.5 px-4 py-3 rounded-xl bg-brand-50 border border-brand-100">
                    <Award size={15} className="text-brand-600 shrink-0" />
                    <span className="text-[12px] font-semibold text-brand-700">Weekly report delivered every Sunday, 9:00 PM IST — straight to your Telegram</span>
                  </div>
                </div>
              </ScrollReveal>

              <ScrollReveal className="order-1 lg:order-2">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-600 text-[11px] font-semibold uppercase tracking-[0.12em] mb-4">
                  <Route size={12} /> Trips & Driver Reports
                </div>
                <h3 className="font-heading text-[26px] md:text-[32px] font-extrabold text-ink tracking-[-0.02em] leading-[1.2]">
                  Every journey logged. Every week scored.
                </h3>
                <p className="mt-4 text-[14px] text-ink-soft leading-[1.75] font-body">
                  Trips start and end themselves — no buttons to press. Drive above 5 km/h and a trip
                  begins; idle for 5 minutes and it closes with the full route, distance and duration
                  saved. Every Sunday, CarTrack turns the week into a driver safety score.
                </p>
                <ul className="mt-6 space-y-3">
                  {[
                    "Automatic trip detection with start/end times, duration and distance in km",
                    "Full route replay on the map with green start & red end markers",
                    "0–100 weekly safety score computed from overspeed events & top speed",
                    "Trip count, total km, average & top speed — 8 weeks of history retained",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-5 h-5 rounded-full bg-gradient-to-tr from-emerald-500 to-emerald-400 text-white flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                        <Check size={11} strokeWidth={3} />
                      </span>
                      <span className="text-[13px] text-ink-mid font-body leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </ScrollReveal>
            </div>

            {/* ── Deep dive 5+6: Sharing & Multi-vehicle (compact pair) ── */}
            <div className="grid md:grid-cols-2 gap-8">
              <ScrollReveal>
                <div className="h-full bg-white border border-surface-border rounded-2xl p-8 card-hover-lift hover:border-brand-200/60">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-brand-500/10 to-accent-cyan/10 text-brand-600 flex items-center justify-center mb-6 border border-brand-500/10">
                    <Share2 size={20} />
                  </div>
                  <h3 className="text-[18px] font-heading font-bold text-ink mb-3">Live Location Sharing</h3>
                  <p className="text-[13px] text-ink-soft leading-[1.7] font-body mb-5">
                    Generate a secure, self-expiring link (15, 30 or 60 minutes) that anyone can open in a
                    browser — no account, no app install. Perfect for “I'm on my way” moments,
                    valet handoffs, or customer deliveries. Revoke any active link with one tap.
                  </p>
                  <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-surface-soft border border-surface-border font-mono text-[12px]">
                    <span className="text-brand-600 font-bold truncate">cartrack.app/s/a8f3e2…</span>
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-ink-soft shrink-0 ml-3">
                      <Clock size={11} /> 14:32 left
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-surface-muted overflow-hidden">
                    <div className="h-full w-[70%] rounded-full bg-gradient-to-r from-brand-500 to-accent-cyan" />
                  </div>
                </div>
              </ScrollReveal>

              <ScrollReveal delayClass="reveal-delay-200">
                <div className="h-full bg-white border border-surface-border rounded-2xl p-8 card-hover-lift hover:border-brand-200/60">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-brand-500/10 to-accent-cyan/10 text-brand-600 flex items-center justify-center mb-6 border border-brand-500/10">
                    <Languages size={20} />
                  </div>
                  <h3 className="text-[18px] font-heading font-bold text-ink mb-3">Multi-Vehicle & Bilingual</h3>
                  <p className="text-[13px] text-ink-soft leading-[1.7] font-body mb-5">
                    Link every car in the family or fleet to one account and switch between them with the
                    device picker. The entire dashboard — and every Telegram alert — is available in
                    English and Hindi, with more Indian languages on the roadmap.
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    {["Swift Dzire", "Creta", "Bolero Pickup"].map((v, i) => (
                      <span key={v} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold border ${i === 0 ? "bg-brand-600 text-white border-brand-600 shadow-brand" : "bg-white text-ink-soft border-surface-border"}`}>
                        <Car size={11} /> {v}
                      </span>
                    ))}
                    <span className="ml-auto inline-flex rounded-full border border-surface-border overflow-hidden text-[11px] font-bold">
                      <span className="px-3 py-1.5 bg-brand-600 text-white">EN</span>
                      <span className="px-3 py-1.5 bg-white text-ink-soft">हिं</span>
                    </span>
                  </div>
                </div>
              </ScrollReveal>
            </div>

          </div>
        </div>
      </section>

      {/* ==================== SYSTEM ARCHITECTURE INFOGRAPHIC ==================== */}
      <section className="py-24 bg-surface-soft border-y border-surface-border relative">
        <div className="max-w-7xl mx-auto px-6">
          <ScrollReveal>
            <div className="text-center max-w-xl mx-auto mb-16">
              <span className="inline-block text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-600 mb-3 px-3 py-1 rounded-full bg-brand-50 border border-brand-100">Architecture</span>
              <h2 className="font-heading text-[36px] md:text-[44px] font-extrabold text-ink tracking-[-0.02em] leading-[1.15]">
                System Architecture & Data Flow
              </h2>
              <p className="mt-4 text-[15px] text-ink-soft leading-[1.65] font-body">
                A visual guide to how CarTrack securely connects your headunit to the cloud and owner dashboard.
              </p>
            </div>
          </ScrollReveal>
          
          <ScrollReveal delayClass="reveal-delay-100">
            <div className="max-w-4xl mx-auto rounded-2xl border border-white/5 bg-slate-950 p-2 md:p-4 overflow-hidden flex justify-center shadow-glow-md relative">
              {/* background decorative light orb */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-accent-cyan/10 rounded-full blur-[80px] -z-0" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-500/10 rounded-full blur-[80px] -z-0" />
              <img
                src={infographic}
                alt="CarTrack GPS Tracking Architecture Infographic"
                className="w-full h-auto rounded-xl max-w-3xl object-contain hover:scale-[1.01] transition-transform duration-500 relative z-10"
              />
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ==================== SECTION 4: DASHBOARD SHOWCASE ==================== */}
      <section id="showcase" className="py-28 bg-white relative scroll-mt-18">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            
            <div className="lg:col-span-7 flex justify-center relative">
              {/* soft ambient light behind mockup */}
              <div className="absolute w-[400px] h-[400px] bg-brand-500/10 rounded-full blur-[80px] -z-10" />
              <ScrollReveal className="w-full">
                <div className="rounded-2xl border border-surface-border shadow-card-lg p-2 bg-white/70 backdrop-blur-md">
                  <div className="rounded-xl overflow-hidden">
                    <DashboardMockup />
                  </div>
                </div>
              </ScrollReveal>
            </div>

            <div className="lg:col-span-5 space-y-8">
              <ScrollReveal>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-50 border border-brand-100 text-brand-600 text-[11px] font-semibold uppercase tracking-[0.12em] mb-4">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-600 animate-pulse" />
                  <span>Enterprise Platform</span>
                </div>
                
                <h2 className="font-heading text-[36px] sm:text-[44px] font-extrabold text-ink tracking-[-0.02em] leading-[1.15]">
                  Complete System Visibility
                </h2>
                
                <p className="mt-4 text-[15px] text-ink-soft leading-[1.7] font-body">
                  Connect multiple devices to one dashboard. See exactly where your vehicles are operating, review speeds, and track location paths on dynamic vector maps.
                </p>
              </ScrollReveal>

              <ScrollReveal delayClass="reveal-delay-200">
                <div className="grid grid-cols-3 gap-6 pt-8 border-t border-surface-border">
                  {[
                    { value: "99.9", suffix: "%", label: "Uptime SLA" },
                    { value: "100", suffix: "K+", label: "GPS Events" },
                    { value: "50", suffix: "+", label: "Countries" },
                  ].map(stat => (
                    <div key={stat.label}>
                      <div className="font-mono text-[32px] font-black text-brand-600 tracking-tight leading-none">
                        <CountingStat value={stat.value} suffix={stat.suffix} />
                      </div>
                      <div className="text-[10px] font-semibold text-ink-muted uppercase tracking-wider mt-2 font-body">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </ScrollReveal>
            </div>

          </div>
        </div>
      </section>

      {/* ==================== SECTION 5: HOW IT WORKS ==================== */}
      <section className="py-24 bg-surface-soft relative overflow-hidden">
        {/* Glow effect */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-brand-500/[0.03] rounded-full blur-[80px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          
          <ScrollReveal>
            <div className="text-center max-w-xl mx-auto mb-16">
              <span className="inline-block text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-600 mb-3 px-3 py-1 rounded-full bg-brand-50 border border-brand-100">Setup</span>
              <h2 className="font-heading text-[36px] md:text-[44px] font-extrabold text-ink tracking-[-0.02em] leading-[1.15]">
                Get Tracking In 3 Steps
              </h2>
              <p className="mt-4 text-[15px] text-ink-soft leading-[1.65] font-body">
                A self-contained onboarding process that turns your Android console into an active locator.
              </p>
            </div>
          </ScrollReveal>

          {/* Connected Timeline */}
          <div className="relative grid md:grid-cols-3 gap-8">
            {/* Timeline connectors for desktop */}
            <div className="hidden md:block absolute top-[64px] left-[15%] right-[15%] h-[2px] bg-gradient-to-r from-brand-300 via-accent-cyan/40 to-brand-300/10 -z-10" />

            {[
              { num: "01", title: "Install on Head Unit", desc: "Download the background tracking engine APK from Google Play or direct download. Launch it on your car dashboard console." },
              { num: "02", title: "Pair Your Vehicle", desc: "Grant location permissions and connect to 4G/5G or Wi-Fi. Copy your unique Device ID from the tracker screen." },
              { num: "03", title: "Monitor Anywhere", desc: "Go to Dashboard → Link Device. Enter the Device ID to start receiving live maps and instant Telegram notifications." },
            ].map((step, i) => (
              <ScrollReveal key={i} delayClass={`reveal-delay-${i * 150}`}>
                <div className="relative bg-white/70 backdrop-blur-md border border-surface-border rounded-2xl p-8 card-hover-lift hover:border-brand-200/50 hover:shadow-card-lg h-full">
                  <span className="absolute top-4 right-5 font-heading text-[68px] font-black text-slate-100 leading-none select-none pointer-events-none transition-colors group-hover:text-brand-500/10">
                    {step.num}
                  </span>
                  <div className="relative">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-brand-600 to-accent-cyan flex items-center justify-center mb-6 shadow-brand">
                      <span className="text-[14px] font-heading font-extrabold text-white">{step.num}</span>
                    </div>
                    <h3 className="text-[16px] font-heading font-bold text-ink mb-3">{step.title}</h3>
                    <p className="text-[13px] text-ink-soft leading-[1.7] font-body">{step.desc}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>

        </div>
      </section>

      {/* ==================== SECTION 6: BENEFITS ==================== */}
      <section className="py-28 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            
            <div className="lg:col-span-5 space-y-8">
              <ScrollReveal>
                <h2 className="font-heading text-[36px] sm:text-[44px] font-extrabold text-ink tracking-[-0.02em] leading-[1.15]">
                  Designed for Everyday Safety & Logistics
                </h2>
                
                <p className="mt-4 text-[15px] text-ink-soft leading-[1.7] font-body">
                  CarTrack integrates fully with standard Android operating systems. It runs silently in the background, consuming minimal resources, and starts transmitting telemetry on ignition triggers automatically.
                </p>
              </ScrollReveal>

              <div className="space-y-5">
                {[
                  "Real-time coordinate transmission speed limits control",
                  "Automated instant warning dispatches to Telegram",
                  "Full history export of all weekly speedings and trip routes",
                  "Zero proprietary hardware requirements (works on any Android panel)",
                  "Completely self-managed account deletion controls"
                ].map((benefit, idx) => (
                  <ScrollReveal key={idx} delayClass={`reveal-delay-${idx * 100}`}>
                    <div className="flex items-start gap-3.5">
                      <span className="w-6 h-6 rounded-full bg-gradient-to-tr from-emerald-500 to-emerald-400 text-white flex items-center justify-center shrink-0 mt-0.5 shadow-md border border-emerald-300">
                        <Check size={12} strokeWidth={3} />
                      </span>
                      <span className="text-[14px] font-medium text-ink-mid font-body">{benefit}</span>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7 bg-surface-soft border border-surface-border rounded-3xl p-8 relative overflow-hidden flex flex-col justify-center">
              {/* decorative soft gradient overlay */}
              <div className="absolute top-0 right-0 w-80 h-80 bg-brand-500/[0.04] rounded-full blur-[80px]" />
              <ScrollReveal className="flex flex-col gap-5 relative z-10">
                <div className="bg-white/70 backdrop-blur-md border border-surface-border p-5 rounded-2xl shadow-card-sm flex items-center justify-between hover:border-brand-200/50 hover:shadow-card-md transition-all duration-300">
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-tr from-brand-500/10 to-brand-500/20 text-brand-600 flex items-center justify-center border border-brand-500/10 shadow-sm">
                      <MapPin size={18} />
                    </div>
                    <div>
                      <div className="text-[13px] font-bold text-ink font-heading">Dynamic Coordinate Logging</div>
                      <div className="text-[11px] text-accent-emerald font-bold font-body">Every 8 seconds</div>
                    </div>
                  </div>
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-accent-emerald bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-full">active</span>
                </div>

                <div className="bg-white/70 backdrop-blur-md border border-surface-border p-5 rounded-2xl shadow-card-sm flex items-center justify-between hover:border-brand-200/50 hover:shadow-card-md transition-all duration-300">
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-tr from-sky-500/10 to-sky-500/20 text-sky-500 flex items-center justify-center border border-sky-500/10 shadow-sm">
                      <Bell size={18} />
                    </div>
                    <div>
                      <div className="text-[13px] font-bold text-ink font-heading">Telegram Notifications Gateway</div>
                      <div className="text-[11px] text-ink-soft font-body">Auto links to @CarTrackBot</div>
                    </div>
                  </div>
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-brand-600 bg-brand-50 border border-brand-100 px-2.5 py-1 rounded-full">configured</span>
                </div>

                <div className="bg-white/70 backdrop-blur-md border border-surface-border p-5 rounded-2xl shadow-card-sm flex items-center justify-between hover:border-brand-200/50 hover:shadow-card-md transition-all duration-300">
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-tr from-emerald-500/10 to-emerald-500/20 text-emerald-600 flex items-center justify-center border border-emerald-500/10 shadow-sm">
                      <ShieldCheck size={18} />
                    </div>
                    <div>
                      <div className="text-[13px] font-bold text-ink font-heading">Local Data Privacy Protocol</div>
                      <div className="text-[11px] text-ink-soft font-body">Instant purge button available</div>
                    </div>
                  </div>
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-600 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-full">secure</span>
                </div>
              </ScrollReveal>
            </div>

          </div>
        </div>
      </section>

      {/* ==================== SECTION 7: STATS (DARK) ==================== */}
      <section className="py-24 bg-mesh-gradient text-white relative overflow-hidden">
        {/* Floating gradient lights */}
        <div className="particle-orb top-1/2 left-1/4 -translate-y-1/2 w-80 h-80 bg-brand-600/15" />
        <div className="particle-orb top-1/2 right-1/4 -translate-y-1/2 w-80 h-80 bg-accent-cyan/15" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 text-center">
            {[
              { value: "100", suffix: "K+", label: "Locations Tracked" },
              { value: "10", suffix: "K+", label: "Trips Logged" },
              { value: "99.9", suffix: "%", label: "Platform Availability" },
              { value: "50", suffix: "+", label: "Countries Supported" },
            ].map((stat, idx) => (
              <ScrollReveal key={idx} delayClass={`reveal-delay-${idx * 100}`}>
                <div className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md shadow-glow-sm hover:border-white/25 transition-all duration-300">
                  <div className="font-mono text-[48px] sm:text-[56px] md:text-[64px] font-black text-white tracking-tight leading-none bg-gradient-to-tr from-brand-300 via-white to-accent-cyan bg-clip-text text-transparent">
                    <CountingStat value={stat.value} suffix={stat.suffix} />
                  </div>
                  <div className="mt-4 text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400 font-body">{stat.label}</div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== SECTION 8: SECURITY ==================== */}
      <section className="py-28 bg-white border-y border-surface-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            
            <div className="lg:col-span-6 space-y-6">
              <ScrollReveal>
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-50 border border-emerald-100">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-emerald animate-pulse" />
                  <span className="text-[10px] font-extrabold uppercase tracking-widest text-emerald-700 font-body">Data sovereignty</span>
                </div>
                
                <h2 className="mt-4 font-heading text-[36px] sm:text-[44px] font-extrabold text-ink tracking-[-0.02em] leading-[1.15]">
                  Your Vehicle Data Is Protected
                </h2>
                
                <p className="mt-4 text-[15px] text-ink-soft leading-[1.7] font-body">
                  We believe in total privacy sovereignty. Your tracking coordinates are transmitted strictly via TLS HTTPS connections, isolated, and permanently purgable by you at any time.
                </p>
              </ScrollReveal>
            </div>

            <div className="lg:col-span-6 grid sm:grid-cols-2 gap-6">
              {[
                { icon: Key, title: "JWT Authorization", desc: "Secure JSON Web Token access control blocks unauthorized telemetry probes." },
                { icon: Lock, title: "Encrypted Telemetry", desc: "All coordinates are encoded in transit via secure HTTPS endpoints." },
                { icon: CloudLightning, title: "Secure Gateway APIs", desc: "Strict schema checks and validation run on FastAPI routers." },
                { icon: Database, title: "Privacy Deletions", desc: "Purge all vehicles, paths, accounts, and contact records instantly." }
              ].map((sec, idx) => {
                const Icon = sec.icon;
                return (
                  <ScrollReveal key={idx} delayClass={`reveal-delay-${idx * 100}`}>
                    <div className="bg-surface-soft/60 border border-surface-border rounded-2xl p-6 hover:border-emerald-300/40 hover:bg-emerald-50/20 hover:shadow-card-md transition-all duration-300 group cursor-default">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-emerald-500/10 to-emerald-400/20 text-emerald-600 flex items-center justify-center mb-5 border border-emerald-500/10 transition-colors group-hover:bg-gradient-to-tr group-hover:from-emerald-500 group-hover:to-emerald-400 group-hover:text-white">
                        <Icon size={18} strokeWidth={2} />
                      </div>
                      <h4 className="text-[14px] font-heading font-bold text-ink mb-2">{sec.title}</h4>
                      <p className="text-[12px] text-ink-soft leading-[1.65] font-body">{sec.desc}</p>
                    </div>
                  </ScrollReveal>
                );
              })}
            </div>

          </div>
        </div>
      </section>

      {/* ==================== SECTION 9: TESTIMONIALS ==================== */}
      <section className="py-24 bg-surface-soft">
        <div className="max-w-7xl mx-auto px-6">
          
          <ScrollReveal>
            <div className="text-center max-w-xl mx-auto mb-16">
              <span className="inline-block text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-600 mb-3 px-3 py-1 rounded-full bg-brand-50 border border-brand-100">Testimonials</span>
              <h2 className="font-heading text-[36px] md:text-[44px] font-extrabold text-ink tracking-[-0.02em] leading-[1.15]">
                Loved by Everyday Drivers
              </h2>
              <p className="mt-4 text-[15px] text-ink-soft leading-[1.65] font-body">
                Hear what vehicle owners and fleet managers have to say about the platform.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, idx) => (
              <ScrollReveal key={idx} delayClass={`reveal-delay-${idx * 150}`}>
                <div className="h-full bg-white/80 backdrop-blur-md border border-surface-border rounded-2xl p-8 flex flex-col justify-between hover:shadow-card-lg hover:border-brand-200/50 transition-all duration-300 relative">
                  {/* quote icon watermark */}
                  <div className="absolute top-6 right-8 text-[96px] font-black text-slate-100 font-heading leading-none select-none pointer-events-none">“</div>
                  <div className="flex gap-0.5 mb-5 relative z-10">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-4 h-4 fill-amber-400 text-amber-400" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                      </svg>
                    ))}
                  </div>
                  <p className="text-[14px] text-ink-mid leading-[1.75] font-body italic flex-1 relative z-10">"{t.quote}"</p>
                  <div className="flex items-center gap-3 pt-6 mt-6 border-t border-surface-border relative z-10">
                    <div className={`w-10 h-10 rounded-full ${t.avatarBg} flex items-center justify-center text-[12px] font-bold font-heading shrink-0 shadow-sm border border-white/50`}>
                      {t.avatarInitials}
                    </div>
                    <div>
                      <p className="text-[13px] font-bold text-ink font-heading">{t.name}</p>
                      <p className="text-[11px] text-ink-muted font-body font-medium">{t.role}</p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>

        </div>
      </section>

      {/* ==================== SECTION 11: FAQ ==================== */}
      <section className="py-24 bg-surface-soft border-t border-surface-border">
        <div className="max-w-3xl mx-auto px-6">
          
          <ScrollReveal>
            <div className="text-center max-w-xl mx-auto mb-16">
              <span className="inline-block text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-600 mb-3 px-3 py-1 rounded-full bg-brand-50 border border-brand-100">FAQ</span>
              <h2 className="font-heading text-[36px] md:text-[44px] font-extrabold text-ink tracking-[-0.02em] leading-[1.15]">
                Car GPS Tracking Questions, Answered
              </h2>
              <p className="mt-4 text-[15px] text-ink-soft leading-[1.65] font-body">
                What CarTrack costs, which Android head units it supports, how accurate the
                GPS is, how geofence and SOS alerts work, and what happens to your data.
              </p>
            </div>
          </ScrollReveal>

          <div className="space-y-4">
            {faqs.map((f, idx) => {
              const isOpen = activeFaq === idx;
              return (
                <ScrollReveal key={idx} delayClass={`reveal-delay-${(idx % 4) * 50}`}>
                  <div className="border border-surface-border rounded-2xl overflow-hidden bg-white/70 backdrop-blur-md hover:border-brand-200 hover:shadow-card-md transition-all duration-300">
                    <button
                      onClick={() => setActiveFaq(isOpen ? null : idx)}
                      className="w-full px-6 py-5 flex items-center justify-between text-left gap-4 group cursor-pointer"
                    >
                      <span className="text-[14px] sm:text-[15px] font-bold text-ink group-hover:text-brand-600 transition-colors font-heading leading-tight">{f.q}</span>
                      <div className={`w-7 h-7 rounded-full border border-surface-border flex items-center justify-center shrink-0 transition-all duration-300 ${isOpen ? "border-brand-600 bg-brand-50 rotate-180" : ""}`}>
                        <ChevronDown size={12} className={isOpen ? "text-brand-600" : "text-ink-muted"} />
                      </div>
                    </button>
                    
                    {/* Answers stay mounted whether or not the accordion is
                        open. Unmounting them hid 19 of 20 answers from
                        Google's renderer, which then disagreed with the
                        prerendered HTML. Collapse is height-only. */}
                    <motion.div
                      className="overflow-hidden"
                      initial={false}
                      animate={{ height: isOpen ? "auto" : 0 }}
                      transition={{ duration: 0.25, ease: "easeInOut" }}
                    >
                      <div className="px-6 pb-6 text-[13px] sm:text-[14px] text-ink-soft leading-[1.75] font-body border-t border-surface-border pt-4">
                        {f.a}
                      </div>
                    </motion.div>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>

        </div>
      </section>

      {/* Ad Banner - Bottom Placement */}
      <section className="max-w-4xl mx-auto px-6 my-8">
        <AdBanner testid="splash-ad-bottom" />
      </section>

      {/* ==================== SECTION 12: FINAL CTA ==================== */}
      <section className="py-24 bg-surface-soft">
        <div className="max-w-7xl mx-auto px-6">
          <ScrollReveal>
            <div className="relative rounded-3xl overflow-hidden bg-slate-950 border border-white/5 p-12 md:p-20 text-center shadow-glow-md">
              {/* animated decorative background orbs */}
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center_top,rgba(99,102,241,0.18),transparent_65%)]" />
              <div className="particle-orb bottom-0 left-1/2 -translate-x-1/2 w-96 h-40 bg-brand-600/15" />
              
              <div className="relative z-10 max-w-2xl mx-auto">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-400 mb-5 font-body">Get Started Today</p>
                <h2 className="font-heading text-[38px] sm:text-[52px] font-extrabold text-white tracking-[-0.025em] leading-[1.1]">
                  Protect Your Vehicle.<br />Start in 5 Minutes.
                </h2>
                <p className="mt-5 text-[15px] text-slate-300 leading-[1.7] font-body max-w-md mx-auto">
                  Join thousands of operators and drivers who secure their trips with CarTrack.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link to="/signup" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-white text-ink text-[14px] font-bold hover:bg-slate-100 transition-colors group shadow-lg cursor-pointer shimmer-btn">
                    Get Started Free <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                  <a href="mailto:cartrackerindia26@gmail.com" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl border border-white/10 text-white/70 text-[14px] font-semibold hover:text-white hover:border-white/20 transition-all duration-200 cursor-pointer backdrop-blur-md bg-white/5">
                    Contact Sales
                  </a>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ==================== FOOTER ==================== */}
      <footer className="bg-slate-950 border-t border-white/5 py-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-brand-600 to-accent-cyan flex items-center justify-center shadow-brand">
              <img src="/logo192.png" alt="CarTrack Logo" className="w-4.5 h-4.5 object-contain" />
            </div>
            <span className="text-[14px] font-heading font-extrabold text-white group-hover:text-brand-400 transition-colors">CarTrack</span>
          </Link>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[12px] font-medium text-slate-400">
            <Link to="/faq" className="hover:text-white transition-colors">FAQ</Link>
            <Link to="/user-manual" className="hover:text-white transition-colors">Setup Guide</Link>
            <Link to="/about-us" className="hover:text-white transition-colors">About</Link>
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy</Link>
            <a href={siteContent.site.playUrl} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Google Play</a>
          </div>
          <p className="text-[11px] text-slate-500 font-body font-medium">© {new Date().getFullYear()} CarTrack</p>
        </div>
      </footer>

    </div>
  );
}
